/**
 * 承認ワークフロータブ
 *
 * 承認待ち一覧と自分の申請一覧を表示
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Delete as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { salesApprovalsApi, ApprovalRequest } from '../../services/salesApi';
import ApprovalDetailDialog from './ApprovalDetailDialog';
import { formatDateJST } from '../../utils/dateUtils';

interface ApprovalWorkflowTabProps {
  onRefresh?: () => void;
}

const ApprovalWorkflowTab: React.FC<ApprovalWorkflowTabProps> = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [myRequests, setMyRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [pendingRes, myRes] = await Promise.all([
        salesApprovalsApi.getPendingApprovals(),
        salesApprovalsApi.getMyRequests(),
      ]);

      if (pendingRes.data.success) {
        setPendingApprovals(pendingRes.data.data);
      }
      if (myRes.data.success) {
        setMyRequests(myRes.data.data);
      }
    } catch (err: any) {
      console.error('データ取得エラー:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (caseId: number) => {
    await salesApprovalsApi.approve(caseId);
    fetchData();
    onRefresh?.();
  };

  const handleReject = async (caseId: number, comment: string) => {
    await salesApprovalsApi.reject(caseId, comment);
    fetchData();
    onRefresh?.();
  };

  const handleCancel = async (caseId: number) => {
    if (!window.confirm('申請を取り消しますか？')) return;
    try {
      await salesApprovalsApi.cancel(caseId);
      fetchData();
      onRefresh?.();
    } catch (err: any) {
      setError(err.response?.data?.message || '取消に失敗しました');
    }
  };

  const handleViewDetail = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const getRequestTypeLabel = (type?: string) => {
    switch (type) {
      case 'phase_change':
        return 'フェーズ変更';
      case 'resource_change':
        return '金額変更';
      case 'phase_and_amount_change':
        return 'フェーズ・金額変更';
      case 'initial':
        return '新規登録';
      default:
        return '変更';
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return formatDateJST(dateStr);
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '-';
    return `¥${(amount / 10000).toFixed(0)}万`;
  };

  const renderPendingApprovals = () => (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>案件番号</TableCell>
            <TableCell>案件名</TableCell>
            <TableCell>取引先</TableCell>
            <TableCell>変更種別</TableCell>
            <TableCell>申請者</TableCell>
            <TableCell>申請日</TableCell>
            <TableCell>金額</TableCell>
            <TableCell align="center">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pendingApprovals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  承認待ちの依頼はありません
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            pendingApprovals.map((request) => (
              <TableRow key={request.case_id} hover>
                <TableCell>{request.case_no}</TableCell>
                <TableCell>
                  <Typography
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => handleViewDetail(request)}
                  >
                    {request.case_name}
                  </Typography>
                </TableCell>
                <TableCell>{request.account_name}</TableCell>
                <TableCell>
                  <Chip
                    label={getRequestTypeLabel(request.pending_data?.request_type)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{request.requester_name}</TableCell>
                <TableCell>{formatDate(request.approval_requested_at)}</TableCell>
                <TableCell>{formatAmount(request.current_amount)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="案件詳細">
                    <IconButton size="small" onClick={() => navigate(`/sales/cases/${request.case_id}`)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="承認">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleApprove(request.case_id)}
                    >
                      <ApproveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="却下">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleViewDetail(request)}
                    >
                      <RejectIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMyRequests = () => (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>案件番号</TableCell>
            <TableCell>案件名</TableCell>
            <TableCell>変更種別</TableCell>
            <TableCell>承認者</TableCell>
            <TableCell>申請日</TableCell>
            <TableCell>状態</TableCell>
            <TableCell align="center">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {myRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  申請中の依頼はありません
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            myRequests.map((request) => (
              <React.Fragment key={request.case_id}>
                <TableRow hover>
                  <TableCell>{request.case_no}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => handleViewDetail(request)}
                    >
                      {request.case_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRequestTypeLabel(request.pending_data?.request_type)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{request.approver_name}</TableCell>
                  <TableCell>{formatDate(request.approval_requested_at)}</TableCell>
                  <TableCell>
                    {request.approval_status === 'rejected' ? (
                      <Chip label="却下" size="small" color="error" />
                    ) : request.approval_status === 'pending' || request.approval_status === 'new_pending' ? (
                      <Chip label="承認待ち" size="small" color="warning" />
                    ) : request.approval_status === 'draft' ? (
                      <Chip label="下書き" size="small" />
                    ) : (
                      <Chip label={request.approval_status} size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="案件詳細">
                      <IconButton size="small" onClick={() => navigate(`/sales/cases/${request.case_id}`)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(request.approval_status === 'pending' || request.approval_status === 'new_pending') && (
                      <Tooltip title="取消">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancel(request.case_id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                {request.approval_status === 'rejected' && request.approval_comment && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 1, borderBottom: '2px solid', borderColor: 'error.light' }}>
                      <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                        <strong>却下理由:</strong> {request.approval_comment}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              承認依頼
              {pendingApprovals.length > 0 && (
                <Chip label={pendingApprovals.length} size="small" color="error" />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              自分の申請
              {myRequests.length > 0 && (
                <Chip label={myRequests.length} size="small" color="primary" />
              )}
            </Box>
          }
        />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && renderPendingApprovals()}
        {activeTab === 1 && renderMyRequests()}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={fetchData} variant="outlined" size="small">
          更新
        </Button>
      </Box>

      <ApprovalDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        request={selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
        isApprover={activeTab === 0}
      />
    </Box>
  );
};

export default ApprovalWorkflowTab;
